import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
    getTicketBookingById,
    getBookingPaymentStatus
} from "../../../../app/store/slices/ticket/ticketBuying";
import { getEventBySlug } from "../../../../app/store/slices/event/event";
import { formatEventDate } from "../../../../utils/dateFormatter";
import { EVENT_TYPES } from "../../../../hooks/event-hooks/useEventStatus";
import { Body2 } from "../../../../themes/typography";


/**
 * TODO: Make a 3 functions:
 * 1. to download tickets
 * 2. to download tickets invoice
 * 3. to download booking fees invoice
 * All are depends on backend.
 * 
 * TODO: Once the PG integration is done, handle the payment failure/success scenario properly.
 * 
 * 173 bookingId to test
 */

export default function useTicketOrderPayment() {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    console.log("User in useTicketOrderPayment:", user);

    const { bookingId } = useParams();

    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState(null);

    const open = Boolean(anchorEl);


    /**
     * Fetch ticket booking details based on bookingId from URL params.
     * 1. First, verify the payment status. If payment is successful, proceed to fetch booking details.
     * 2. Then, fetch the event details using the event_slug from the booking data.
     * 3. Handle loading and error states appropriately.
     * Wrapped in useEffect to run on component mount and when bookingId changes.
     */
    useEffect(() => {
        async function fetchTicketBooking() {
            try {
                setLoading(true);

                if (bookingId && user?.id) {
                    const paymentStatus = await dispatch(getBookingPaymentStatus(bookingId)).unwrap();
                    // console.log("Payment Status:", paymentStatus);

                    if (paymentStatus?.transaction?.payment_status === 'SUCCESS') {

                        const data = await dispatch(getTicketBookingById(bookingId)).unwrap();

                        if (data.code === 200 && data.success) {
                            const eventData = await dispatch(getEventBySlug(data.data.event_slug)).unwrap();

                            if (eventData.code === 200 && eventData.success) {
                                setOrderData({
                                    orderData: data.data,
                                    eventData: eventData.data
                                });
                            }
                        }
                    }
                    else {
                        setError(paymentStatus?.pg_response?.code);
                    }
                }
            } catch (error) {
                // console.error("Error fetching ticket booking:", error);
                setError(error?.code);
            } finally {
                setLoading(false);
            }
        }

        fetchTicketBooking();
    }, [bookingId, dispatch, user?.id]);

    // console.log("Order Data:", JSON.stringify(orderData));


    /**
     * Get the formats, categories and subcategories (fcs) of the event in a single array.
     */
    const fcs = useMemo(() => {
        if (orderData) {
            const result = [];
            if (orderData.eventData?.formats?.[0]) result.push(orderData.eventData.formats[0]);
            if (orderData.eventData?.subcategories?.[0]) result.push(orderData.eventData.subcategories[0]);
            if (orderData.eventData?.category) result.push(orderData.eventData.category);

            // Sort by length (descending)
            return result.sort((a, b) => b.length - a.length);
        }
        return [];
    }, [orderData?.eventData?.formats, orderData?.eventData?.category, orderData?.eventData?.subcategories]);


    /**
     * Check how many tickets are booked in this order.
     */
    const numberOfTickets = useMemo(() => {
        if (orderData) {
            return orderData.orderData?.ticket_type_info
                .flatMap(ticket => ticket.tickets)
                .filter(subTicket => subTicket.parent_id === null)
                .length || 0;
        }
    }, [orderData]);


    /**
     * Get the event timing information.
     * Wrapped in useCallback to memoize the function and prevent unnecessary re-creations on every render.
     */
    const eventTimingUI = useCallback(() => {
        if (!orderData) return null;
        const eventType = orderData?.eventData?.event_type;
        const startTime = orderData?.eventData?.start_time;
        const endTime = orderData?.eventData?.end_time;

        if (eventType === EVENT_TYPES.singleDay || eventType === EVENT_TYPES.continuous) {
            return `${startTime} - ${endTime}`;
        } else {
            return (
                <div style={{ display: "flex", gap: "5px" }}>
                    <Body2>Multiple Days</Body2>
                    {/* <Body2
                        color="var(--tertiary-color)"
                        style={{ cursor: "pointer" }}
                        onClick={scrollToEventSchedule}
                    >
                        [View Event Schedule]
                    </Body2> */}
                </div>
            );
        }
    }, [orderData]);


    /**
     * Get the event duration information.
     * Wrapped in useCallback to memoize the function and prevent unnecessary re-creations on every render.
     */
    const eventDurationUI = useCallback(() => {
        if (!orderData) return null;

        const {
            event_type: eventType,
            start_date: startDate,
            end_date: endDate,
            start_time: startTime,
            end_time: endTime,
            schedules
        } = orderData.eventData;

        const startDateTime = dayjs(`${startDate} ${startTime}`);
        const endDateTime = dayjs(`${endDate} ${endTime}`);
        const durationMinutes = endDateTime.diff(startDateTime, "minute");

        // Handle Single-Day Event
        if (eventType === EVENT_TYPES.singleDay) {
            const firstSchedule = schedules?.[0];

            if (firstSchedule?.slots?.length > 0) {
                return (
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <Body2>Multiple Slots</Body2>
                        {/* <Body2
                            color="var(--tertiary-color)"
                            style={{ cursor: 'pointer' }}
                            onClick={scrollToEventSchedule}
                        >
                            [View Event Schedule]
                        </Body2> */}
                    </div>
                );
            }

            return <Body2>{durationMinutes} minutes</Body2>;
        }

        if (eventType === EVENT_TYPES.continuous) {
            const hours = Math.floor(durationMinutes / 60);
            const minutes = durationMinutes % 60;
            return <Body2>{hours > 0 ? `${hours} hour ` : ""}{minutes > 0 ? `${minutes} minutes` : ''}</Body2>;
        }

        // For other/unknown case
        return null;
    }, [orderData])


    /**
     * Get the event date and show them in different format based on event types.
     * Wrapped in useMemo to optimize performance by memoizing the result.
     */
    const eventDate = useMemo(() => {
        if (orderData) {
            const eventType = orderData.eventData?.event_type;
            const start = formatEventDate(orderData.eventData?.start_date);
            const end = formatEventDate(orderData.eventData?.end_date);
            switch (eventType) {
                case EVENT_TYPES.singleDay:
                    return start;
                case EVENT_TYPES.multiDay:
                    return `${start} - ${end}`;
                case EVENT_TYPES.continuous:
                    return `${start} to ${end}`;
                default:
                    return `${start} to ${end}`;
            }
        }
        return null;
    }, [
        orderData?.eventData?.event_type,
        orderData?.eventData?.start_date,
        orderData?.eventData?.end_date
    ]);


    /**
     * Function to handle menu open action
     */
    function handleMenuOpen(event) {
        setAnchorEl(event.currentTarget);
    }


    /**
     * Function to handle menu close action
     */
    function handleMenuClose() {
        setAnchorEl(null);
    }


    /**
     * Function to handle invoice download
     * For now, it just closes the menu
     */
    function handleDownloadInvoice() {
        setAnchorEl(null);
        /**
         * TODO: Implement invoice download logic in future
         */
    }


    /**
     * Function to handle ticket download
     */
    function handleDownloadTickets() {
        /**
         * TODO: Implement ticket download logic in future
         */
    }

    // console.log("Loading -> ", loading)
    // console.log("Error -> ", error)

    return {
        open,
        anchorEl,
        handleMenuOpen,
        handleMenuClose,
        handleDownloadInvoice,
        handleDownloadTickets,
        loading,
        bookingId,
        orderData,
        fcs,
        numberOfTickets,
        eventTimingUI,
        eventDurationUI,
        eventDate,
        error
    };
}

/**
 * Details to map into QR Code
 * 1. ticketId
 * 2. ticketTypeId
 * 3. bookingId
 */